import {
  EFillPolicy,
  EFillPolicyValue,
  EOrderExpiry,
  EOrderState,
  EOrderStateCentroid,
  EOrderType
} from '@aqx-core/common/api/order';
import { TAppLanguageISOCountryCode, i18n } from '@aqx-core/common/i18n';
import { EUIBuySellType, EUIOrderTypeLabel } from '@aqx-core/common/services/trade/form/types';

export const mapOrderTypeLabelAndDirectionToOrderType = (
  orderTypeLabel: EUIOrderTypeLabel,
  direction?: EUIBuySellType
) => {
  const BUY_ORDER_TYPE_MAP: Partial<Record<EUIOrderTypeLabel, EOrderType>> = {
    [EUIOrderTypeLabel.MARKET]: EOrderType.BUY,
    [EUIOrderTypeLabel.MARKET_TO_LIMIT]: EOrderType.BUY_MARKET_TO_LIMIT,
    [EUIOrderTypeLabel.LIMIT]: EOrderType.BUY_LIMIT,
    [EUIOrderTypeLabel.STOP]: EOrderType.BUY_STOP,
    [EUIOrderTypeLabel.STOP_LIMIT]: EOrderType.BUY_STOP_LIMIT
  };
  const SELL_ORDER_TYPE_MAP: Partial<Record<EUIOrderTypeLabel, EOrderType>> = {
    [EUIOrderTypeLabel.MARKET]: EOrderType.SELL,
    [EUIOrderTypeLabel.MARKET_TO_LIMIT]: EOrderType.SELL_MARKET_TO_LIMIT,
    [EUIOrderTypeLabel.LIMIT]: EOrderType.SELL_LIMIT,
    [EUIOrderTypeLabel.STOP]: EOrderType.SELL_STOP,
    [EUIOrderTypeLabel.STOP_LIMIT]: EOrderType.SELL_STOP_LIMIT
  };
  if (direction === undefined || direction === null) {
    return undefined;
  }
  switch (direction) {
    case EUIBuySellType.BUY:
      return BUY_ORDER_TYPE_MAP[orderTypeLabel];
    case EUIBuySellType.SELL:
      return SELL_ORDER_TYPE_MAP[orderTypeLabel];
    default:
      return undefined;
  }
};

export const mapFillPolicyIntToText = (fillPolicyInt: EFillPolicy, lng?: TAppLanguageISOCountryCode) => {
  const FILL_POLICY_INT_TEXT_MAP: Record<EFillPolicy, () => string> = {
    [EFillPolicy.FOK]: () => i18n.t('Fill or Kill', { lng }),
    [EFillPolicy.IOC]: () => i18n.t('Immediate or Cancel', { lng }),
    [EFillPolicy.RETURN]: () => i18n.t('Return', { lng }),
    [EFillPolicy.GTC]: () => i18n.t('Good Till Canceled', { lng }),
    [EFillPolicy.GTD]: () => i18n.t('Good Till Date', { lng }),
    [EFillPolicy.DAY]: () => i18n.t('Day', { lng })
  };
  return FILL_POLICY_INT_TEXT_MAP[fillPolicyInt]?.() ?? '';
};

export const mapFillPolicyValueToText = (fillPolicyValue: EFillPolicyValue, lng?: TAppLanguageISOCountryCode) => {
  const FILL_POLICY_VALUE_TEXT_MAP: Record<EFillPolicyValue, () => string> = {
    [EFillPolicyValue.FOK]: () => i18n.t('Fill or Kill', { lng }),
    [EFillPolicyValue.IOC]: () => i18n.t('Immediate or Cancel', { lng }),
    [EFillPolicyValue.RETURN]: () => i18n.t('Return', { lng }),
    [EFillPolicyValue.GTC]: () => i18n.t('Good Till Canceled', { lng }),
    [EFillPolicyValue.GTD]: () => i18n.t('Good Till Date', { lng }),
    [EFillPolicyValue.DAY]: () => i18n.t('Day', { lng })
  };
  return FILL_POLICY_VALUE_TEXT_MAP[fillPolicyValue]?.() ?? '';
};

export const mapPriceFieldLabel = (orderTypeLabel?: EUIOrderTypeLabel, lng?: TAppLanguageISOCountryCode) => {
  const PRICE_FIELD_LABEL_MAP: Partial<Record<EUIOrderTypeLabel, string>> = {
    [EUIOrderTypeLabel.STOP]: i18n.t('Stop Price', { lng }),
    [EUIOrderTypeLabel.LIMIT]: i18n.t('Limit Price', { lng }),
    [EUIOrderTypeLabel.STOP_LIMIT]: i18n.t('Limit Price', { lng }),
    [EUIOrderTypeLabel.MARKET]: i18n.t('Market Price', { lng }),
    [EUIOrderTypeLabel.MARKET_TO_LIMIT]: i18n.t('Market Price', { lng })
  };
  if (orderTypeLabel === undefined || orderTypeLabel === null) {
    return '--';
  }
  return PRICE_FIELD_LABEL_MAP[orderTypeLabel];
};

export const mapTradeExpiryValueToText = (tradeExpiryValue: EOrderExpiry, lng?: TAppLanguageISOCountryCode) => {
  const TEXT_MAP: Partial<Record<EOrderExpiry, () => string>> = {
    [EOrderExpiry.GTC]: () => i18n.t('Good Till Canceled', { lng }),
    [EOrderExpiry.GTD]: () => i18n.t('Good Till Day', { lng }),
    [EOrderExpiry.SPECIFIED_DAY]: () => i18n.t('Specified Date', { lng }),
    [EOrderExpiry.SPECIFIED_TIMESTAMP]: () => i18n.t('Specified Date and Time', { lng })
  };
  return TEXT_MAP[tradeExpiryValue]?.() ?? '';
};

export const orderStateTextMapCentroid: Record<EOrderStateCentroid, () => string> = {
  [EOrderStateCentroid.CREATED]: () => i18n.t('Created'),
  [EOrderStateCentroid.NEW]: () => i18n.t('New'),
  [EOrderStateCentroid.FILLED]: () => i18n.t('Filled'),
  [EOrderStateCentroid.PARTIALLY_FILLED]: () => i18n.t('Partially Filled'),
  [EOrderStateCentroid.REJECTED]: () => i18n.t('Rejected'),
  [EOrderStateCentroid.CANCELED]: () => i18n.t('Canceled'),
  [EOrderStateCentroid.PARTIAL]: () => i18n.t('Partial'),
  [EOrderStateCentroid.MODIFIED]: () => i18n.t('Modified'),
  [EOrderStateCentroid.TRIGGERED]: () => i18n.t('Triggered'),
  [EOrderStateCentroid.PROCESSING]: () => i18n.t('Processing'),
  [EOrderStateCentroid.CANCELLED]: () => i18n.t('Cancelled'),
  [EOrderStateCentroid.DONE]: () => i18n.t('Done'),
  [EOrderStateCentroid.PENDING]: () => i18n.t('Pending')
};

export const orderStateTextMap: Record<EOrderState, () => string> = {
  [EOrderState.OPEN_NORMAL]: () => i18n.t('Open Normal'),
  [EOrderState.OPEN_REMAND]: () => i18n.t('Open Remand'),
  [EOrderState.OPEN_RESTORED]: () => i18n.t('Open Restored'),
  [EOrderState.CLOSED_NORMAL]: () => i18n.t('Close Normal'),
  [EOrderState.CLOSED_PART]: () => i18n.t('Closed Partial'),
  [EOrderState.CLOSED_BY]: () => i18n.t('Closed By'),
  [EOrderState.DELETED]: () => i18n.t('Deleted'),
  [EOrderState.STARTED]: () => i18n.t('Started'),
  [EOrderState.PLACED]: () => i18n.t('Placed'),
  [EOrderState.CANCELED]: () => i18n.t('Canceled'),
  [EOrderState.PARTIAL]: () => i18n.t('Partially Filled'),
  [EOrderState.FILLED]: () => i18n.t('Filled'),
  [EOrderState.REJECTED]: () => i18n.t('Rejected'),
  [EOrderState.EXPIRED]: () => i18n.t('Expired'),
  [EOrderState.REQUEST_ADD]: () => i18n.t('Request Add'),
  [EOrderState.REQUEST_MODIFY]: () => i18n.t('Request Modify'),
  [EOrderState.REQUEST_CANCEL]: () => i18n.t('Request Cancel')
};
